import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { getBillingContext } from "../../../api/client";
import { FEATURE_PLATFORM_CONSOLIDATED_DASHBOARD_ENABLED } from "../../../constants/featureFlags";
import { useAuthContext } from "../../../context/AuthContext";
import { PLATFORM_CONSOLE_SEGMENT } from "./platformConsoleRoutes";

/** Ordem do menu (alinhada ao backend `PLATFORM_CONSOLE_SECTION_KEYS`). */
export const PLATFORM_CONSOLE_SECTION_ORDER = [
  PLATFORM_CONSOLE_SEGMENT.workspaces,
  PLATFORM_CONSOLE_SEGMENT.opportunities,
  PLATFORM_CONSOLE_SEGMENT.insights,
  PLATFORM_CONSOLE_SEGMENT.dashboard,
  PLATFORM_CONSOLE_SEGMENT.productUsage,
  PLATFORM_CONSOLE_SEGMENT.passengerTicketSales,
  PLATFORM_CONSOLE_SEGMENT.presence,
  PLATFORM_CONSOLE_SEGMENT.segments,
  PLATFORM_CONSOLE_SEGMENT.governance,
  PLATFORM_CONSOLE_SEGMENT.catalog,
  PLATFORM_CONSOLE_SEGMENT.chatbotTemplates,
  PLATFORM_CONSOLE_SEGMENT.pendingActions,
  PLATFORM_CONSOLE_SEGMENT.platformErrors,
  PLATFORM_CONSOLE_SEGMENT.apifacilInstances,
  PLATFORM_CONSOLE_SEGMENT.finance,
  PLATFORM_CONSOLE_SEGMENT.internalAsk,
];

/** Seções ocultas temporariamente (menu, rotas e matriz de governança). ACL persistida é preservada. */
export const PLATFORM_CONSOLE_HIDDEN_SECTION_KEYS = new Set([
  ...(FEATURE_PLATFORM_CONSOLIDATED_DASHBOARD_ENABLED ? [] : [PLATFORM_CONSOLE_SEGMENT.dashboard]),
]);

/** Seções exibidas na matriz de permissões da Governança. */
export const PLATFORM_CONSOLE_GOVERNANCE_SECTION_ORDER = PLATFORM_CONSOLE_SECTION_ORDER.filter(
  (k) => !PLATFORM_CONSOLE_HIDDEN_SECTION_KEYS.has(k)
);

/** Alinhado ao backend `isPlatformSuperEmail` / WORKSPACE_PLATFORM_MASTER_EMAIL. */
const PLATFORM_SUPER_EMAIL_DEFAULT = "xvision@xvisiontecnologia.com.br";

function normalizeEmail(v) {
  return String(v || "")
    .trim()
    .toLowerCase();
}

function resolvePlatformSuperEmail() {
  const fromEnv = String(process.env.REACT_APP_WORKSPACE_PLATFORM_MASTER_EMAIL || "").trim();
  return normalizeEmail(fromEnv || PLATFORM_SUPER_EMAIL_DEFAULT);
}

function isKnownPlatformSuperEmail(email) {
  const target = resolvePlatformSuperEmail();
  const em = normalizeEmail(email);
  return Boolean(em && em === target);
}

const defaultSectionsAllTrue = () => {
  const o = {};
  PLATFORM_CONSOLE_SECTION_ORDER.forEach((k) => {
    o[k] = { view: true, create: true, edit: true, delete: true };
  });
  return o;
};

const defaultSectionsAllFalse = () => {
  const o = {};
  PLATFORM_CONSOLE_SECTION_ORDER.forEach((k) => {
    o[k] = { view: false, create: false, edit: false, delete: false };
  });
  return o;
};

const PlatformConsoleAclContext = createContext({
  loading: true,
  superUser: false,
  sections: defaultSectionsAllFalse(),
  financePerson: null,
  refresh: async () => {},
  can: () => false,
});

export function PlatformConsoleAclProvider({ children, enabled = true }) {
  const { userData } = useAuthContext();
  const sessionIsSuper = isKnownPlatformSuperEmail(userData?.email);
  const [loading, setLoading] = useState(() => Boolean(enabled));
  const [superUser, setSuperUser] = useState(() => sessionIsSuper);
  const [financePerson, setFinancePerson] = useState(null);
  // Fail-closed até o contexto de billing confirmar as seções (super global ignora).
  const [sections, setSections] = useState(() =>
    sessionIsSuper ? defaultSectionsAllTrue() : defaultSectionsAllFalse()
  );

  const load = useCallback(async () => {
    if (!enabled) {
      setLoading(false);
      setSuperUser(sessionIsSuper);
      setFinancePerson(null);
      setSections(sessionIsSuper ? defaultSectionsAllTrue() : defaultSectionsAllFalse());
      return;
    }
    setLoading(true);
    const emailSuper = isKnownPlatformSuperEmail(userData?.email);
    try {
      const ctx = await getBillingContext();
      const caps = ctx?.capabilities || {};
      const raw = caps.platform_console_sections;
      const apiSuper = Boolean(caps.platform_console_super);
      const effectiveSuper = apiSuper || emailSuper;
      setSuperUser(effectiveSuper);
      setFinancePerson(caps.finance_person && caps.finance_person.id ? caps.finance_person : null);
      if (effectiveSuper) {
        setSections(defaultSectionsAllTrue());
      } else if (raw && typeof raw === "object") {
        const next = defaultSectionsAllFalse();
        PLATFORM_CONSOLE_SECTION_ORDER.forEach((k) => {
          const frag = raw[k];
          if (frag && typeof frag === "object") {
            next[k] = {
              view: Boolean(frag.view),
              create: Boolean(frag.create),
              edit: Boolean(frag.edit),
              delete: Boolean(frag.delete),
            };
          }
        });
        setSections(next);
      } else {
        // Sem payload de seções: legado (ACL nula no backend) = acesso total master.
        setSections(defaultSectionsAllTrue());
      }
    } catch {
      // Se /billing/context falhar: master global continua com acesso total no UI.
      // Demais masters: allow-all no UI; a API segue impondo ACL em /billing/admin/*.
      setSuperUser(emailSuper);
      setSections(defaultSectionsAllTrue());
      setFinancePerson(null);
    } finally {
      setLoading(false);
    }
  }, [enabled, sessionIsSuper, userData?.email]);

  useEffect(() => {
    load();
  }, [load]);

  const can = useCallback(
    (section, action) => {
      if (PLATFORM_CONSOLE_HIDDEN_SECTION_KEYS.has(section)) return false;
      if (superUser || sessionIsSuper) return true;
      const s = sections?.[section];
      return Boolean(s?.[action]);
    },
    [sections, superUser, sessionIsSuper]
  );

  const value = useMemo(
    () => ({
      loading,
      superUser,
      sections,
      financePerson,
      refresh: load,
      can,
    }),
    [loading, superUser, sections, financePerson, load, can]
  );

  return <PlatformConsoleAclContext.Provider value={value}>{children}</PlatformConsoleAclContext.Provider>;
}

export function usePlatformConsoleAcl() {
  return useContext(PlatformConsoleAclContext);
}
